import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDistance } from 'date-fns';

import ReplyIcon from '@mui/icons-material/Reply';
import EditIcon from '@mui/icons-material/EditOutlined';
import MoreVerticalIcon from '@mui/icons-material/MoreVert';
import { MenuItem, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import UploadIcon from '@mui/icons-material/CloudUploadOutlined';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteEmptyIcon from '@mui/icons-material/FavoriteBorder';

import AppEntityAvatar, {
  AvatarSize,
} from '../../../designe/app-entity-avatar/AppEntityAvatar';
import AppFile from '../../../designe/app-file/AppFile';
import AppMenu, {
  getPosition,
  PositionOption,
} from '../../../designe/app-menu/AppMenu';
import AppTextField from '../../../designe/app-text-field/AppTextField';
import AppButton from '../../../designe/app-button/AppButton';
import Comment from '../comment/Comment';
import AppButtonUploadFile from '../../../designe/app-button-upload-files/AppButtonUploadFile';
import SendIcon from '@mui/icons-material/Send';
import clsx from 'clsx';
import { useStyles } from './Post.styles';
import theme from '../../../../styles/theme';
import { SvgClass } from '../../../../styles/mixins';
import AppDocsViewer from '../../../designe/app-docs-viewer/AppDocsViewer';
import { IComment } from '../../../../types/interfaces/comment';
import { IPost } from '../../../../types/interfaces/post';
import { usePost } from './usePost';
import { orderBy } from '../../../../utils/array-utils';

interface IPostProps {
  post: IPost;
  editable?: boolean;
  repliedPost?: boolean;
  onPostReplied?: any;
  allowComment?: boolean;
}

export default function Post(props: IPostProps) {
  const {
    post,
    editable = true,
    repliedPost = false,
    onPostReplied,
    allowComment = true,
  } = props;
  const [anchorEl, setAnchorEl] = useState(null);
  const { t } = useTranslation();
  const { classes } = useStyles(theme);
  const [selectedFile, setSelectedFile] = useState(null);

  const {
    onPostEdit,
    onToggleToEditPost,
    onCommentEdit,
    onCommentReply,
    onCommentDeleteFromReply,
    onPostSave,
    onPostDelete,
    onCommentSave,
    onPostReaction,
    onUploadFiles,
    onDeleteFile,
    onDeleteCommentFile,
    onUploadCommentFiles,
    onShowMore,
    editedPost,
    newComment,
    commentFiles,
    postFiles,
    userReaction,
    user,
    showedCommentsCount,
  } = usePost(post);

  function isEditable() {
    if (!editable) return false;

    return (
      !repliedPost &&
      !editedPost &&
      (post.ownerId === user.id || post.pageUserId === user.id)
    );
  }

  function getPostReactionIcon() {
    return userReaction ? (
      <FavoriteIcon
        onClick={() => onPostReaction()}
        className={SvgClass.active}
      />
    ) : (
      <FavoriteEmptyIcon onClick={() => onPostReaction()} />
    );
  }

  return (
    <div
      className={clsx(
        classes.postWrapper,
        !repliedPost && classes.postWrapperShadow,
      )}>
      <div className={classes.header}>
        <AppEntityAvatar entity={post.owner} size={AvatarSize.m} />
        <div className={classes.userInfo}>
          <Typography variant="text-lg">
            {`${post.owner.firstname} ${post.owner.lastname}`}
          </Typography>
          <Typography variant="text-sm" className={classes.createdAt}>
            {formatDistance(post.createdAt, new Date(), { addSuffix: true })}
          </Typography>
        </div>
        {isEditable() && (
          <div>
            <AppMenu
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={getPosition(
                PositionOption.bottom,
                PositionOption.right,
              )}
              transformOrigin={getPosition(
                PositionOption.top,
                PositionOption.right,
              )}
              targetElement={
                <MoreVerticalIcon
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                />
              }>
              {post.ownerId === user.id && (
                <MenuItem
                  className={classes.menuItem}
                  onClick={() => {
                    onToggleToEditPost();
                    setAnchorEl(null);
                  }}>
                  <EditIcon />
                  {t('post.editPost')}
                </MenuItem>
              )}
              <MenuItem
                className={classes.menuItem}
                onClick={() => {
                  onPostDelete();
                  setAnchorEl(null);
                }}>
                <DeleteIcon className={classes.delete} />
                {t('post.removePost')}
              </MenuItem>
            </AppMenu>
          </div>
        )}
      </div>
      <div className={classes.content}>
        {!editedPost && <div>{post.content}</div>}
        {editedPost && (
          <div className={classes.editedPostContent}>
            <AppTextField
              fullWidth
              multiline
              maxRows={5}
              value={editedPost.content}
              onChange={(e) => onPostEdit(e.target.value)}
            />
            {!!postFiles?.length && (
              <div className={classes.filesWrapper}>
                {postFiles.map((file) => (
                  <AppFile
                    key={file.id}
                    file={file}
                    onDelete={(file) => onDeleteFile(file.id)}
                  />
                ))}
              </div>
            )}
            <div className={classes.editedPostBottom}>
              <AppButton
                variant="outlined"
                component="label"
                className={classes.uploadFileButton}>
                <input
                  autoComplete="off"
                  multiple
                  style={{ display: 'none' }}
                  tabIndex={-1}
                  type="file"
                  onChange={(e) => onUploadFiles(e.target.files)}
                />
                <UploadIcon />
                {t('home.uploadFiles')}
              </AppButton>

              <AppButton
                onClick={onPostSave}
                disabled={!editedPost.content && !postFiles.length}>
                {t('common.save')}
              </AppButton>
            </div>
          </div>
        )}
        {!editedPost && !!postFiles.length && (
          <div className={classes.filesWrapper}>
            {postFiles.map((file) => (
              <AppFile
                key={file.id}
                file={file}
                onClick={() => {
                  setSelectedFile(file);
                }}
              />
            ))}
          </div>
        )}
        {post.parent && !repliedPost && (
          <div className={classes.repliedPostWrapper}>
            <Post post={post.parent} repliedPost />
          </div>
        )}
      </div>
      {!repliedPost && !editedPost && (
        <>
          <div className={classes.footer}>
            {getPostReactionIcon()}
            <ReplyIcon onClick={() => onPostReplied(post.id)} />
          </div>

          {!!post.comments?.length && (
            <Typography mt={2} variant="h3">
              {t('post.comments')}({post.comments.length})
            </Typography>
          )}
          <div
            className={clsx(
              classes.commentsWrapper,
              (allowComment || !!post.comments.length) &&
                classes.commentsWrapperBorder,
            )}>
            <div>
              {orderBy<IComment>(post.comments, 'createdAt', 'desc')
                .slice(0, showedCommentsCount)
                .map((comment: IComment) => (
                  <Comment
                    comment={comment}
                    key={comment.id}
                    onCommentReply={onCommentReply}
                    onCommentDeleteFromReply={onCommentDeleteFromReply}
                    allowReply={allowComment}
                  />
                ))}
              {showedCommentsCount < post.comments.length && (
                <AppButton variant={'text'} onClick={() => onShowMore()}>
                  {t('post.showMore')}
                </AppButton>
              )}
            </div>
            {allowComment && (
              <div className={classes.newCommentWrapper}>
                <div className={classes.newCommentData}>
                  <AppTextField
                    placeholder={t('user.newComment')}
                    fullWidth
                    multiline
                    maxRows={5}
                    value={newComment.content}
                    onChange={(e) => onCommentEdit(e.target.value)}
                  />

                  <AppButtonUploadFile
                    onFileInput={(e) => onUploadCommentFiles(e.target.files)}
                  />
                  <SendIcon
                    onClick={onCommentSave}
                    className={clsx(
                      !newComment.content &&
                        !commentFiles.length &&
                        SvgClass.disabled,
                    )}
                  />
                </div>
                {!!newComment.parent && (
                  <div className={classes.answeredCommentWrapper}>
                    <Comment comment={newComment.parent} repliedComment />
                  </div>
                )}

                {!!commentFiles.length && (
                  <div className={classes.uploadedFilesWrapper}>
                    {commentFiles.map((file) => (
                      <AppFile
                        key={file.id}
                        file={file}
                        onDelete={(file) => onDeleteCommentFile(file.id)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          {selectedFile && (
            <AppDocsViewer
              files={postFiles}
              selectedFile={selectedFile}
              setSelectedFile={setSelectedFile}
            />
          )}
        </>
      )}
    </div>
  );
}
